import { useCurrentUser } from '@artic/api-client';

/**
 * Resolves the active partner from the current user's partner list.
 *
 * @returns The partner matching the active value by key or name, or the first partner when no match is found.
 */
function useActivePartner() {
  const { partners, activePartner } = useCurrentUser();

  return (
    partners.find((partner) => partner.partner_key === activePartner) ??
    partners.find((partner) => partner.partner_name === activePartner) ??
    partners[0]
  );
}

/**
 * Retrieves the UUID of the active partner.
 *
 * @returns The active partner's UUID, or `undefined` when no partner is selected.
 */
export function usePartnerId(): string | undefined {
  return useActivePartner()?.partner_id;
}

/**
 * Gets the full name of the currently active partner.
 *
 * @returns The active partner's full name, or `undefined` if no partner is available.
 */
export function usePartnerName(): string | undefined {
  return useActivePartner()?.partner_name;
}

/**
 * Retrieves the selected partner's week-start setting.
 *
 * @returns `0` or `1` for the selected partner's week-start setting, or `undefined` when no partner is selected
 */
export function usePartnerWeekStartsOn(): 0 | 1 | undefined {
  return useActivePartner()?.week_starts_on;
}
